'use strict';

import { stemmer } from 'porter-stemmer';

// The Monash FODMAP capture v2 (plan §5.7): categories, certified
// manufacturers, and 1,732 foods with per-serve traffic lights, hosted on our
// CDN and fetched lazily the first time a curator opens the browser. Levels
// are 1/2/3 = green/amber/red; slot 9 is the serve grams. Rebuild with
// migrations/rebuild-monash-capture.js from a fresh HAR.
const CAPTURE_URL = 'https://static.chewba.info/assets/189e1867-bec5-4026-8247-6e1d8afaed2a.json';

// fodmap array slots 1-8 mapped to our nutrient codes; slot 7 is Monash's
// total-oligos rollup, which we don't store
export const SLOT_NUTRNOS = ['FRC', 'LAC', 'SOR', 'MAN', 'OLF', 'OLG', null, 'PLY'];

let capturePromise = null;

export function fetchCapture() {
    capturePromise =
        capturePromise ||
        fetch(CAPTURE_URL).then((response) => response.json());

    return capturePromise;
}

export function imageUrl(imageId) {
    return `https://www.monashfodmap.net/apin/v1/food/forType/1/imageid/${imageId}`;
}

const tokenize = (text) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// Names tokenize + stem once per foods array; the search runs entirely in
// memory so the index is just parallel token/stem lists
const indexes = new WeakMap();

function indexFor(foods) {
    if (!indexes.has(foods)) {
        indexes.set(
            foods,
            foods.map((food) => {
                const tokens = tokenize(food.name);

                return { tokens, stems: tokens.map(stemmer) };
            })
        );
    }

    return indexes.get(foods);
}

// Every query term must land somewhere in the name (AND); rank by how it
// landed — exact token > prefix > shared stem — then by keyword density so
// short names that are mostly match outrank long names that merely contain
// the terms.
export function searchFoods(foods, query, limit = 12) {
    const terms = tokenize(query);

    if (!terms.length) {
        return [];
    }

    const stems = terms.map(stemmer);
    const index = indexFor(foods);

    return foods
        .map((food, foodIdx) => {
            const { tokens, stems: nameStems } = index[foodIdx];
            const matched = new Set();
            let quality = 0;

            for (let termIdx = 0; termIdx < terms.length; termIdx++) {
                let best = 0;
                let bestToken = -1;

                tokens.forEach((token, tokenIdx) => {
                    const grade =
                        token === terms[termIdx] ? 3
                        : token.startsWith(terms[termIdx]) ? 2
                        : nameStems[tokenIdx] === stems[termIdx] ? 1
                        : 0;

                    if (grade > best) {
                        best = grade;
                        bestToken = tokenIdx;
                    }
                });

                if (!best) {
                    return null;
                }

                quality += best;
                matched.add(bestToken);
            }

            return { food, quality, density: matched.size / tokens.length };
        })
        .filter(Boolean)
        .sort(
            (a, b) =>
                b.quality - a.quality ||
                b.density - a.density ||
                a.food.name.localeCompare(b.food.name)
        )
        .map((scored) => scored.food)
        .slice(0, limit);
}

const serveGrams = (serve) => serve.measurement_value ?? serve.fodmap[8];

// The FODMAP entry tool records "level N at S grams" per nutrient; derive that
// threshold from the food's serves: the smallest amber serve wins, else the
// smallest red serve, else the largest green serve at level 0 (tested green).
export function serveThreshold(food, nutrNo) {
    const slot = SLOT_NUTRNOS.indexOf(nutrNo);

    const serves = (food.serves || []).filter(
        (serve) => Array.isArray(serve?.fodmap) && serveGrams(serve) > 0
    );

    if (slot === -1 || !serves.length) {
        return null;
    }

    for (const monashLevel of [2, 3]) {
        const flagged = serves.filter((serve) => serve.fodmap[slot] === monashLevel);

        if (flagged.length) {
            const smallest = flagged.reduce((a, b) => (serveGrams(a) <= serveGrams(b) ? a : b));

            return { size: serveGrams(smallest), level: monashLevel - 1 };
        }
    }

    const green = serves.filter((serve) => serve.fodmap[slot] === 1);

    if (!green.length) {
        return null;
    }

    const largest = green.reduce((a, b) => (serveGrams(a) >= serveGrams(b) ? a : b));

    return { size: serveGrams(largest), level: 0 };
}
